import { FormikProps } from 'formik';
import React from 'react';
import InputMask from 'react-input-mask';

import { states } from '../../../../helpers/Location';

import { useBillingAddressSectionBootstrap } from './billingAddressSection.bootstrap.hook';
import { Card, FormRow, SectionTitle } from '../styles';
import { IPaymentLinkFormValues } from '../types/types';

interface BillingAddressSectionProps {
  form: FormikProps<IPaymentLinkFormValues>;
}

export const BillingAddressSection: React.FC<BillingAddressSectionProps> = ({
  form,
}) => {
  const {
    loadingCep,
    cepMessage,
    handleCepChange,
    handleCepBlur,
    zipCodeDescribedBy,
    showZipCodeError,
    showCepMessage,
    showStateError,
    showCityError,
    showAddressError,
    showAddressNumberError,
  } = useBillingAddressSectionBootstrap(form);

  return (
    <Card>
      <SectionTitle>Endereço de cobrança</SectionTitle>

      <FormRow className="three-columns">
        <div className="form-group">
          <label htmlFor="payment-zip-code">
            CEP <span className="text-danger">*</span>
          </label>

          <InputMask
            id="payment-zip-code"
            name="zipCode"
            placeholder="00000-000"
            mask="99999-999"
            maskChar={null}
            type="tel"
            autoComplete="postal-code"
            className={showZipCodeError ? 'is-invalid' : undefined}
            aria-invalid={showZipCodeError}
            aria-describedby={zipCodeDescribedBy}
            value={form.values.zipCode}
            onChange={handleCepChange}
            onBlur={handleCepBlur}
            disabled={loadingCep}
          />

          {loadingCep && (
            <small
              id="payment-zip-code-status"
              className="text-muted"
              role="status"
            >
              Buscando endereço...
            </small>
          )}

          {showZipCodeError && (
            <span
              id="payment-zip-code-error"
              className="invalid-feedback"
              role="alert"
            >
              {form.errors.zipCode}
            </span>
          )}

          {showCepMessage && (
            <small
              id="payment-zip-code-hint"
              className="text-muted"
              role="status"
            >
              {cepMessage}
            </small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="payment-state">
            Estado <span className="text-danger">*</span>
          </label>

          <select
            id="payment-state"
            name="state"
            autoComplete="address-level1"
            className={showStateError ? 'is-invalid' : undefined}
            aria-invalid={showStateError}
            aria-describedby={
              showStateError ? 'payment-state-error' : undefined
            }
            value={form.values.state}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          >
            <option value="" disabled>
              Selecione...
            </option>

            {states.map(({ state, name }) => (
              <option key={state} value={state}>
                {name} - {state}
              </option>
            ))}
          </select>

          {showStateError && (
            <span
              id="payment-state-error"
              className="invalid-feedback"
              role="alert"
            >
              {form.errors.state}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="payment-city">
            Cidade <span className="text-danger">*</span>
          </label>

          <input
            id="payment-city"
            name="city"
            type="text"
            autoComplete="address-level2"
            className={showCityError ? 'is-invalid' : undefined}
            aria-invalid={showCityError}
            aria-describedby={showCityError ? 'payment-city-error' : undefined}
            value={form.values.city}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />

          {showCityError && (
            <span
              id="payment-city-error"
              className="invalid-feedback"
              role="alert"
            >
              {form.errors.city}
            </span>
          )}
        </div>
      </FormRow>

      <FormRow className="three-columns">
        <div className="form-group">
          <label htmlFor="payment-address">
            Endereço <span className="text-danger">*</span>
          </label>

          <input
            id="payment-address"
            name="address"
            type="text"
            autoComplete="address-line1"
            className={showAddressError ? 'is-invalid' : undefined}
            aria-invalid={showAddressError}
            aria-describedby={
              showAddressError ? 'payment-address-error' : undefined
            }
            value={form.values.address}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />

          {showAddressError && (
            <span
              id="payment-address-error"
              className="invalid-feedback"
              role="alert"
            >
              {form.errors.address}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="payment-address-number">
            Número <span className="text-danger">*</span>
          </label>

          <input
            id="payment-address-number"
            name="addressNumber"
            type="text"
            autoComplete="address-line2"
            className={showAddressNumberError ? 'is-invalid' : undefined}
            aria-invalid={showAddressNumberError}
            aria-describedby={
              showAddressNumberError
                ? 'payment-address-number-error'
                : undefined
            }
            value={form.values.addressNumber}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />

          {showAddressNumberError && (
            <span
              id="payment-address-number-error"
              className="invalid-feedback"
              role="alert"
            >
              {form.errors.addressNumber}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="payment-address-complement">Complemento</label>

          <input
            id="payment-address-complement"
            name="addressComplement"
            type="text"
            autoComplete="address-line3"
            value={form.values.addressComplement}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
        </div>
      </FormRow>
    </Card>
  );
};
